import React, { useState, useRef, useEffect } from 'react';
import Layout from '../../Component/Layout';
import {
    FaRupeeSign, FaShoppingCart, FaFilter as FaFunnel,
    FaBullseye, FaUsers, FaClipboardList, FaCalendarAlt
} from 'react-icons/fa';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { PURCHASE_API } from '../../apiConfig';

// ==========================================
// DATE RANGE HELPERS (same logic as CFO Dashboard)
// ==========================================

// Returns first and last day of the current month, as JS Date objects.
function getCurrentMonthRange() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // last day of month
    return { start, end };
}

// Formats a Date -> Date range as "Aug 1 – Aug 31, 2026"
function formatRangeLabel(start, end) {
    if (!start || !end) return 'Select date range';
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} – ${endStr}`;
}

// Format a JS Date as "YYYY-MM-DD" for the API (local date, not UTC-shifted)
function toApiDateString(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

// ==========================================
// STATIC / MOCK DATA
// ==========================================
// "Sales (This Month)" is the only KPI wired to live data below (see
// getSalesThisMonthKpi). The rest stay as mock placeholders for now.
const baseKpis = [
    { label: 'Sales (This Month)', value: '₹ 12.48 Cr', change: '+18.6% vs Apr \'25', up: true, icon: FaRupeeSign, bg: '#2563eb' },
    { label: 'Orders Booked', value: '₹ 15.32 Cr', change: '+21.4% vs Apr \'25', up: true, icon: FaShoppingCart, bg: '#16a34a' },
    { label: 'Pipeline Value', value: '₹ 48.75 Cr', change: '+14.2% vs Apr \'25', up: true, icon: FaFunnel, bg: '#7c3aed' },
    { label: 'Win Rate', value: '32.7%', change: '+3.6 pp vs Apr \'25', up: true, icon: FaBullseye, bg: '#ea580c' },
    { label: 'Active Customers', value: '286', change: '+6.1% vs Apr \'25', up: true, icon: FaUsers, bg: '#0284c7' },
    { label: 'Avg. Order Value', value: '₹ 4.32 L', change: '+7.3% vs Apr \'25', up: true, icon: FaClipboardList, bg: '#dc2626' },
];

// Fallback/mock — shown until the live trend loads (or if the fetch fails).
const mockSalesTrend = [
    { month: 'Dec', thisYear: 5.1, prevYear: 6.8 },
    { month: 'Jan', thisYear: 6.0, prevYear: 6.2 },
    { month: 'Feb', thisYear: 6.6, prevYear: 6.6 },
    { month: 'Mar', thisYear: 8.4, prevYear: 7.0 },
    { month: 'Apr', thisYear: 10.5, prevYear: 8.2 },
    { month: 'May', thisYear: 12.48, prevYear: 9.0 },
    { month: 'Jun', thisYear: null, prevYear: 9.6 },
    { month: 'Jul', thisYear: null, prevYear: 10.1 },
    { month: 'Aug', thisYear: null, prevYear: 10.4 },
    { month: 'Sep', thisYear: null, prevYear: 10.9 },
    { month: 'Oct', thisYear: null, prevYear: 11.6 },
    { month: 'Nov', thisYear: null, prevYear: 12.1 },
];

const productCategory = [
    { name: 'Power Cables', value: 5.25, pct: 42.1, color: '#2563eb' },
    { name: 'Control Cables', value: 2.85, pct: 22.8, color: '#16a34a' },
    { name: 'Instrumentation Cables', value: 1.95, pct: 15.6, color: '#f97316' },
    { name: 'Wires & Flexible Cables', value: 1.40, pct: 11.2, color: '#dc2626' },
    { name: 'Others', value: 1.03, pct: 8.3, color: '#9ca3af' },
];

const topCustomers = [
    { name: 'L&T Construction', value: 1.85 },
    { name: 'Reliance Industries', value: 1.52 },
    { name: 'Tata Power', value: 1.18 },
    { name: 'Adani Infra', value: 1.05 },
    { name: 'Vedanta Ltd.', value: 0.92 },
];

const orderBookingByRep = [
    { rep: 'Arjun Mehta', value: 2.45, pct: 16.0, change: '+28.4%', up: true },
    { rep: 'Priya Nair', value: 2.10, pct: 13.7, change: '+15.3%', up: true },
    { rep: 'Rahul Verma', value: 1.95, pct: 12.7, change: '+22.1%', up: true },
    { rep: 'Sneha Iyer', value: 1.75, pct: 11.4, change: '+10.7%', up: true },
    { rep: 'Vikram Singh', value: 1.60, pct: 10.4, change: '+8.6%', up: true },
    { rep: 'Karan Patel', value: 1.40, pct: 9.1, change: '-2.4%', up: false },
    { rep: 'Neha Gupta', value: 1.25, pct: 8.1, change: '+5.7%', up: true },
    { rep: 'Saurabh Das', value: 0.97, pct: 6.3, change: '-6.1%', up: false },
    { rep: 'Others', value: 0.90, pct: 5.9, change: '+3.2%', up: true },
];
const orderBookingTotal = { value: 15.32, pct: 100, change: '+21.4%', up: true };

const pipelineStages = [
    { name: 'Qualification', value: 12.36, pct: 25, color: '#2563eb' },
    { name: 'Need Analysis', value: 14.58, pct: 30, color: '#16a34a' },
    { name: 'Proposal', value: 13.42, pct: 27, color: '#f97316' },
    { name: 'Negotiation', value: 8.39, pct: 17, color: '#7c3aed' },
];

const salesByRegion = [
    { region: 'West', value: 4.12, pct: 33.0 },
    { region: 'South', value: 3.18, pct: 25.5 },
    { region: 'North', value: 2.63, pct: 21.1 },
    { region: 'East', value: 1.68, pct: 13.4 },
    { region: 'Central', value: 0.87, pct: 7.0 },
];
const salesByRegionTotal = { value: 12.48, pct: 100 };

const topProducts = [
    { name: 'XLPE Power Cable', value: 4.25, pct: 34.1, change: '+16.3%', up: true },
    { name: 'PVC Insulated Cable', value: 3.02, pct: 24.2, change: '+11.8%', up: true },
    { name: 'Control Cable', value: 2.21, pct: 17.7, change: '+9.6%', up: true },
    { name: 'Instrumentation Cable', value: 1.39, pct: 11.1, change: '-2.1%', up: false },
    { name: 'Flexible Cable', value: 0.96, pct: 7.7, change: '+5.4%', up: true },
];

const topOpportunities = [
    { name: '400kV Power Cable Project', customer: 'L&T Construction', value: 3.25, close: 'Jun 15, 2025', stage: 'Proposal' },
    { name: 'Solar Infra Cable Supply', customer: 'Adani Green Energy', value: 2.18, close: 'Jun 28, 2025', stage: 'Negotiation' },
    { name: 'Metro Rail Cable Project', customer: 'DMRC', value: 1.85, close: 'Jul 10, 2025', stage: 'Proposal' },
    { name: 'Oil & Gas Cable Supply', customer: 'Reliance Industries', value: 1.62, close: 'Jul 25, 2025', stage: 'Need Analysis' },
    { name: 'Industrial Cable Supply', customer: 'Vedanta Ltd.', value: 1.45, close: 'Jul 18, 2025', stage: 'Negotiation' },
];

const winLoss = [
    { name: 'Won', value: 21, pct: 75.0, color: '#16a34a' },
    { name: 'Lost', value: 7, pct: 25.0, color: '#dc2626' },
];
const winLossTotal = 28;

const stageBadgeStyle = {
    Proposal: { background: '#fef3c7', color: '#92400e' },
    Negotiation: { background: '#ede9fe', color: '#6d28d9' },
    'Need Analysis': { background: '#ccfbf1', color: '#0f766e' },
    Qualification: { background: '#dbeafe', color: '#1d4ed8' },
};

// ==========================================
// PLAIN INLINE STYLES (no Tailwind needed)
// ==========================================
const s = {
    page: { padding: 24, background: '#f8fafc', fontFamily: 'Inter, Arial, sans-serif' },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    title: { fontSize: 26, fontWeight: 700, color: '#111827', margin: 0 },
    subtitle: { fontSize: 13, color: '#6b7280', margin: '2px 0 0' },
    toolbarBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', fontSize: 13, color: '#374151', cursor: 'pointer' },
    toolbarRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
    card: { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    cardHeader: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '16px 20px 12px' },
    cardTitle: { fontSize: 15, fontWeight: 600, color: '#1f2937', margin: 0 },
    cardSubtitle: { fontSize: 12, color: '#9ca3af' },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, marginBottom: 20 },
    kpiIcon: (bg) => ({ width: 40, height: 40, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, marginBottom: 10 }),
    kpiLabel: { fontSize: 13, color: '#6b7280', marginBottom: 4 },
    kpiValue: { fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 },
    kpiChangeUp: { fontSize: 12, color: '#16a34a', fontWeight: 500 },
    kpiChangeDown: { fontSize: 12, color: '#dc2626', fontWeight: 500 },
    grid3: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 20 },
    grid1_2: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 },
    table: { width: '100%', fontSize: 13, borderCollapse: 'collapse' },
    th: { fontSize: 11, color: '#9ca3af', textAlign: 'left', fontWeight: 500, paddingBottom: 8 },
    thRight: { fontSize: 11, color: '#9ca3af', textAlign: 'right', fontWeight: 500, paddingBottom: 8 },
    td: { padding: '6px 0', color: '#374151', borderTop: '1px solid #f3f4f6' },
    tdRight: { padding: '6px 0', textAlign: 'right', color: '#374151', borderTop: '1px solid #f3f4f6' },
    viewAll: { padding: '12px 20px', borderTop: '1px solid #f3f4f6', color: '#2563eb', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
    badge: (style) => ({ fontSize: 11, padding: '2px 10px', borderRadius: 999, fontWeight: 500, ...style }),
};

function fmt(n) { return n.toFixed(2); }

// ==========================================
// COMPONENT
// ==========================================
function CSODashboard() {
    const { start: defaultStart, end: defaultEnd } = getCurrentMonthRange();
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [showPicker, setShowPicker] = useState(false);
    const pickerRef = useRef(null);

    // "Sales (This Month)" KPI — fetched from the backend, replaces the
    // mock entry in baseKpis once loaded.
    const [salesThisMonth, setSalesThisMonth] = useState(null); // { value, change, up } | null

    // "Orders Booked" KPI — fetched from the backend, replaces the mock
    // entry in baseKpis once loaded.
    const [ordersBooked, setOrdersBooked] = useState(null); // { value, change, up } | null

    // "Pipeline Value" KPI — fetched from the backend, replaces the mock
    // entry in baseKpis once loaded.
    const [pipelineValue, setPipelineValue] = useState(null); // { value, change, up } | null

    // "Win Rate" KPI — fetched from the backend, replaces the mock entry
    // in baseKpis once loaded.
    const [winRate, setWinRate] = useState(null); // { value, change, up } | null

    // "Active Customers" KPI — fetched from the backend, replaces the mock
    // entry in baseKpis once loaded.
    const [activeCustomers, setActiveCustomers] = useState(null); // { value, change, up } | null

    // "Avg. Order Value" KPI — fetched from the backend, replaces the mock
    // entry in baseKpis once loaded.
    const [avgOrderValue, setAvgOrderValue] = useState(null); // { value, change, up } | null

    // GET ${SALES_API}/cso-dashboard/sales-this-month?startDate=&endDate=
    // -> { sales: { raw, formatted: { value, unit }, formattedString, deltaPct } }
    useEffect(() => {
        if (!startDate || !endDate) return;

        let cancelled = false;

        const fetchSalesThisMonth = async () => {
            try {
                const { data } = await axios.get(`${PURCHASE_API}/cso-dashboard/sales-this-month`, {
                    params: {
                        startDate: toApiDateString(startDate),
                        endDate: toApiDateString(endDate),
                    },
                });
                if (cancelled || !data?.success) return;

                const { formattedString, deltaPct } = data.sales;
                setSalesThisMonth({
                    value: formattedString,
                    change: deltaPct === null
                        ? 'vs previous period'
                        : `${Math.abs(deltaPct)}% vs previous period`,
                    up: deltaPct === null ? true : deltaPct >= 0,
                });
            } catch (err) {
                console.error('Failed to load Sales (This Month):', err);
            }
        };

        fetchSalesThisMonth();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    // GET ${PURCHASE_API}/cso-dashboard/orders-booked?startDate=&endDate=
    // -> { ordersBooked: { raw, formatted: { value, unit }, formattedString, deltaPct } }
    useEffect(() => {
        if (!startDate || !endDate) return;

        let cancelled = false;

        const fetchOrdersBooked = async () => {
            try {
                const { data } = await axios.get(`${PURCHASE_API}/cso-dashboard/orders-booked`, {
                    params: {
                        startDate: toApiDateString(startDate),
                        endDate: toApiDateString(endDate),
                    },
                });
                if (cancelled || !data?.success) return;

                const { formattedString, deltaPct } = data.ordersBooked;
                setOrdersBooked({
                    value: formattedString,
                    change: deltaPct === null
                        ? 'vs previous period'
                        : `${Math.abs(deltaPct)}% vs previous period`,
                    up: deltaPct === null ? true : deltaPct >= 0,
                });
            } catch (err) {
                console.error('Failed to load Orders Booked:', err);
            }
        };

        fetchOrdersBooked();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    // GET ${PURCHASE_API}/cso-dashboard/pipeline-value?startDate=&endDate=
    // -> { pipelineValue: { raw, formatted: { value, unit }, formattedString, deltaPct } }
    useEffect(() => {
        if (!startDate || !endDate) return;

        let cancelled = false;

        const fetchPipelineValue = async () => {
            try {
                const { data } = await axios.get(`${PURCHASE_API}/cso-dashboard/pipeline-value`, {
                    params: {
                        startDate: toApiDateString(startDate),
                        endDate: toApiDateString(endDate),
                    },
                });
                if (cancelled || !data?.success) return;

                const { formattedString, deltaPct, soValue, rfqValue } = data.pipelineValue;
                // eslint-disable-next-line no-console
                console.log(`[Pipeline Value] SO: ₹${soValue}, RFQ (unconverted): ₹${rfqValue}`);
                setPipelineValue({
                    value: formattedString,
                    change: deltaPct === null
                        ? 'vs previous period'
                        : `${Math.abs(deltaPct)}% vs previous period`,
                    up: deltaPct === null ? true : deltaPct >= 0,
                });
            } catch (err) {
                console.error('Failed to load Pipeline Value:', err);
            }
        };

        fetchPipelineValue();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    // GET ${PURCHASE_API}/cso-dashboard/win-rate?startDate=&endDate=
    // -> { winRate: { raw, deltaPp, won, lost } }
    useEffect(() => {
        if (!startDate || !endDate) return;

        let cancelled = false;

        const fetchWinRate = async () => {
            try {
                const { data } = await axios.get(`${PURCHASE_API}/cso-dashboard/win-rate`, {
                    params: {
                        startDate: toApiDateString(startDate),
                        endDate: toApiDateString(endDate),
                    },
                });
                if (cancelled || !data?.success) return;

                const { raw, deltaPp, won, lost } = data.winRate;
                console.log(`[Win Rate] Won: ${won}, Lost: ${lost}, Rate: ${raw}%`);
                setWinRate({
                    value: `${raw}%`,
                    change: deltaPp === null
                        ? 'vs previous period'
                        : `${Math.abs(deltaPp)} pp vs previous period`,
                    up: deltaPp === null ? true : deltaPp >= 0,
                });
            } catch (err) {
                console.error('Failed to load Win Rate:', err);
            }
        };

        fetchWinRate();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    // GET ${PURCHASE_API}/cso-dashboard/active-customers?startDate=&endDate=
    // -> { activeCustomers: { raw, deltaPct } }
    useEffect(() => {
        if (!startDate || !endDate) return;

        let cancelled = false;

        const fetchActiveCustomers = async () => {
            try {
                const { data } = await axios.get(`${PURCHASE_API}/cso-dashboard/active-customers`, {
                    params: {
                        startDate: toApiDateString(startDate),
                        endDate: toApiDateString(endDate),
                    },
                });
                if (cancelled || !data?.success) return;

                const { raw, deltaPct } = data.activeCustomers;
                setActiveCustomers({
                    value: String(raw),
                    change: deltaPct === null
                        ? 'vs previous period'
                        : `${Math.abs(deltaPct)}% vs previous period`,
                    up: deltaPct === null ? true : deltaPct >= 0,
                });
            } catch (err) {
                console.error('Failed to load Active Customers:', err);
            }
        };

        fetchActiveCustomers();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    // GET ${PURCHASE_API}/cso-dashboard/avg-order-value?startDate=&endDate=
    // -> { avgOrderValue: { raw, formatted: { value, unit }, formattedString, deltaPct } }
    useEffect(() => {
        if (!startDate || !endDate) return;

        let cancelled = false;

        const fetchAvgOrderValue = async () => {
            try {
                const { data } = await axios.get(`${PURCHASE_API}/cso-dashboard/avg-order-value`, {
                    params: {
                        startDate: toApiDateString(startDate),
                        endDate: toApiDateString(endDate),
                    },
                });
                if (cancelled || !data?.success) return;

                const { formattedString, deltaPct } = data.avgOrderValue;
                setAvgOrderValue({
                    value: formattedString,
                    change: deltaPct === null
                        ? 'vs previous period'
                        : `${Math.abs(deltaPct)}% vs previous period`,
                    up: deltaPct === null ? true : deltaPct >= 0,
                });
            } catch (err) {
                console.error('Failed to load Avg. Order Value:', err);
            }
        };

        fetchAvgOrderValue();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    // Merge the live figures into the KPI list, once loaded.
    const kpis = baseKpis.map((k) => {
        if (k.label === 'Sales (This Month)' && salesThisMonth) {
            return { ...k, value: salesThisMonth.value, change: salesThisMonth.change, up: salesThisMonth.up };
        }
        if (k.label === 'Orders Booked' && ordersBooked) {
            return { ...k, value: ordersBooked.value, change: ordersBooked.change, up: ordersBooked.up };
        }
        if (k.label === 'Pipeline Value' && pipelineValue) {
            return { ...k, value: pipelineValue.value, change: pipelineValue.change, up: pipelineValue.up };
        }
        if (k.label === 'Win Rate' && winRate) {
            return { ...k, value: winRate.value, change: winRate.change, up: winRate.up };
        }
        if (k.label === 'Active Customers' && activeCustomers) {
            return { ...k, value: activeCustomers.value, change: activeCustomers.change, up: activeCustomers.up };
        }
        if (k.label === 'Avg. Order Value' && avgOrderValue) {
            return { ...k, value: avgOrderValue.value, change: avgOrderValue.change, up: avgOrderValue.up };
        }
        return k;
    });

    // "Sales Trend (₹ Cr)" chart data — fetched from the backend.
    const [salesTrend, setSalesTrend] = useState(mockSalesTrend);
    const [salesTrendUnit, setSalesTrendUnit] = useState('Cr');

    // GET ${PURCHASE_API}/cso-dashboard/sales-trend?startDate=&endDate=&months=12
    // -> { trend: [{ month, thisYear, prevYear }, ...], unit: "Cr" | "Lakh" | "K" | "" }
    useEffect(() => {
        if (!startDate || !endDate) return;

        let cancelled = false;

        const fetchSalesTrend = async () => {
            try {
                const { data } = await axios.get(`${PURCHASE_API}/cso-dashboard/sales-trend`, {
                    params: {
                        startDate: toApiDateString(startDate),
                        endDate: toApiDateString(endDate),
                        months: 12,
                    },
                });
                if (cancelled || !data?.success) return;
                setSalesTrend(data.trend);
                setSalesTrendUnit(data.unit || '');
            } catch (err) {
                console.error('Failed to load Sales Trend:', err);
            }
        };

        fetchSalesTrend();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    // Close the picker when clicking outside of it
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        if (start && end) {
            setShowPicker(false);
        }
    };

    return (
        <Layout defaultSidebarOpen={false}>
            <div style={s.page}>
                {/* Header */}
                <div style={s.headerRow}>
                    <div>
                        <h1 style={s.title}>CSO Dashboard</h1>
                        <p style={s.subtitle}>Sales Performance at a Glance</p>
                    </div>
                    <div style={s.toolbarRow}>
                        <div style={{ position: 'relative' }} ref={pickerRef}>
                            <button
                                style={s.toolbarBtn}
                                onClick={() => setShowPicker((prev) => !prev)}
                            >
                                <FaCalendarAlt size={13} color="#6b7280" />
                                {formatRangeLabel(startDate, endDate)} ▾
                            </button>

                            {showPicker && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 8px)',
                                        left: 0,
                                        zIndex: 100,
                                        background: '#fff',
                                        borderRadius: 10,
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                                        border: '1px solid #e5e7eb',
                                        padding: 10,
                                    }}
                                >
                                    <DatePicker
                                        selectsRange
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={handleDateChange}
                                        inline
                                        monthsShown={2}
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            gap: 8,
                                            marginTop: 8,
                                        }}
                                    >
                                        <button
                                            onClick={() => {
                                                const { start, end } = getCurrentMonthRange();
                                                setStartDate(start);
                                                setEndDate(end);
                                                setShowPicker(false);
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 8,
                                                padding: '6px 12px',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#374151',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            This Month
                                        </button>
                                        <button
                                            onClick={() => setShowPicker(false)}
                                            style={{
                                                background: '#2563eb',
                                                border: 'none',
                                                borderRadius: 8,
                                                padding: '6px 14px',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#fff',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <button style={s.toolbarBtn}>All Plants ▾</button>
                        <button style={s.toolbarBtn}>🔄 Refresh</button>
                        <button style={s.toolbarBtn}>⬇ Export</button>
                    </div>
                </div>

                {/* KPI Cards */}
                <div style={s.kpiGrid}>
                    {kpis.map((k) => {
                        const Icon = k.icon;
                        return (
                            <div key={k.label} style={{ ...s.card, padding: 18 }}>
                                <div style={s.kpiIcon(k.bg)}><Icon /></div>
                                <div style={s.kpiLabel}>{k.label}</div>
                                <div style={s.kpiValue}>{k.value}</div>
                                <div style={k.up ? s.kpiChangeUp : s.kpiChangeDown}>
                                    {k.up ? '▲' : '▼'} {k.change}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Row 1: Sales Trend / Product Category / Top Customers */}
                <div style={s.grid3}>
                    <div style={s.card}>
                        <div style={s.cardHeader}><h3 style={s.cardTitle}>Sales Trend ({salesTrendUnit ? `₹ ${salesTrendUnit}` : '₹'})</h3></div>
                        <div style={{ height: 220, padding: '0 12px 16px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={salesTrend}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                    <Tooltip formatter={(value, name) => [`₹ ${value} ${salesTrendUnit}`, name === 'thisYear' ? 'This Year' : 'Previous Year']} />
                                    <Line type="monotone" dataKey="thisYear" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} name="This Year" />
                                    <Line type="monotone" dataKey="prevYear" stroke="#9ca3af" strokeDasharray="4 4" strokeWidth={2} dot={false} name="Previous Year" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>Sales by Product Category</h3>
                            <span style={s.cardSubtitle}>(This Month)</span>
                        </div>
                        <div style={{ height: 190 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={productCategory} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                                        {productCategory.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={{ padding: '0 20px 16px' }}>
                            {productCategory.map((p) => (
                                <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#4b5563' }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
                                        {p.name}
                                    </span>
                                    <span style={{ color: '#111827', fontWeight: 500 }}>₹ {fmt(p.value)} Cr ({p.pct}%)</span>
                                </div>
                            ))}
                        </div>
                        <div style={s.viewAll}>View All Products →</div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>Sales by Top 5 Customers</h3>
                            <span style={s.cardSubtitle}>(This Month)</span>
                        </div>
                        <div style={{ height: 220 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={topCustomers} layout="vertical" margin={{ left: 20 }}>
                                    <XAxis type="number" hide />
                                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#4b5563' }} axisLine={false} tickLine={false} />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} barSize={14} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={s.viewAll}>View All Customers →</div>
                    </div>
                </div>

                {/* Row 2: Order Booking / Pipeline / Sales by Region */}
                <div style={s.grid3}>
                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>Order Booking by Sales Representative</h3>
                            <span style={s.cardSubtitle}>(This Month)</span>
                        </div>
                        <div style={{ padding: '0 20px 8px', overflowX: 'auto' }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Sales Rep</th>
                                        <th style={s.thRight}>Orders (₹ Cr)</th>
                                        <th style={s.thRight}>% of Total</th>
                                        <th style={s.thRight}>vs Apr '25</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orderBookingByRep.map((r) => (
                                        <tr key={r.rep}>
                                            <td style={s.td}>{r.rep}</td>
                                            <td style={s.tdRight}>{fmt(r.value)}</td>
                                            <td style={s.tdRight}>{r.pct.toFixed(1)}%</td>
                                            <td style={{ ...s.tdRight, color: r.up ? '#16a34a' : '#dc2626', fontWeight: 500 }}>
                                                {r.up ? '▲' : '▼'} {r.change}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr style={{ fontWeight: 600, color: '#111827' }}>
                                        <td style={s.td}>Total</td>
                                        <td style={s.tdRight}>{fmt(orderBookingTotal.value)}</td>
                                        <td style={s.tdRight}>{orderBookingTotal.pct}%</td>
                                        <td style={{ ...s.tdRight, color: '#16a34a' }}>▲ {orderBookingTotal.change}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div style={s.viewAll}>View All Sales Representatives →</div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}><h3 style={s.cardTitle}>Pipeline Overview</h3></div>
                        <div style={{ display: 'flex', gap: 8, padding: '0 20px 16px', flexWrap: 'wrap' }}>
                            {pipelineStages.map((p) => (
                                <div key={p.name} style={{ flex: 1, minWidth: 100, background: p.color, color: '#fff', borderRadius: 8, padding: 12 }}>
                                    <div style={{ fontSize: 11, opacity: 0.9 }}>{p.name}</div>
                                    <div style={{ fontSize: 17, fontWeight: 700 }}>₹ {fmt(p.value)} Cr</div>
                                    <div style={{ fontSize: 11, opacity: 0.85 }}>{p.pct}%</div>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: 16, padding: '0 20px 16px' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>Total Pipeline Value</div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>₹ 48.75 Cr</div>
                                <div style={{ fontSize: 11, color: '#16a34a' }}>▲ 14.2% vs Apr '25</div>
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>Expected Conversion (Next 3 Months)</div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>₹ 16.34 Cr</div>
                            </div>
                        </div>
                        <div style={s.viewAll}>View Pipeline & Forecast →</div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>Sales by Region</h3>
                            <span style={s.cardSubtitle}>(This Month)</span>
                        </div>
                        <div style={{ padding: '0 20px 8px', overflowX: 'auto' }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Region</th>
                                        <th style={s.thRight}>Sales (₹ Cr)</th>
                                        <th style={s.thRight}>% of Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {salesByRegion.map((r) => (
                                        <tr key={r.region}>
                                            <td style={s.td}>{r.region}</td>
                                            <td style={s.tdRight}>{fmt(r.value)}</td>
                                            <td style={s.tdRight}>{r.pct.toFixed(1)}%</td>
                                        </tr>
                                    ))}
                                    <tr style={{ fontWeight: 600, color: '#111827' }}>
                                        <td style={s.td}>Total</td>
                                        <td style={s.tdRight}>{fmt(salesByRegionTotal.value)}</td>
                                        <td style={s.tdRight}>{salesByRegionTotal.pct}%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Row 3: Top Products / Top Opportunities / Win-Loss */}
                <div style={s.grid3}>
                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>Top Products by Sales</h3>
                            <span style={s.cardSubtitle}>(This Month)</span>
                        </div>
                        <div style={{ padding: '0 20px 8px', overflowX: 'auto' }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Product</th>
                                        <th style={s.thRight}>Sales (₹ Cr)</th>
                                        <th style={s.thRight}>% of Total</th>
                                        <th style={s.thRight}>vs Apr '25</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topProducts.map((p) => (
                                        <tr key={p.name}>
                                            <td style={s.td}>{p.name}</td>
                                            <td style={s.tdRight}>{fmt(p.value)}</td>
                                            <td style={s.tdRight}>{p.pct.toFixed(1)}%</td>
                                            <td style={{ ...s.tdRight, color: p.up ? '#16a34a' : '#dc2626', fontWeight: 500 }}>
                                                {p.up ? '▲' : '▼'} {p.change}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={s.viewAll}>View All Products →</div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}><h3 style={s.cardTitle}>Top Opportunities</h3></div>
                        <div style={{ padding: '0 20px 8px', overflowX: 'auto' }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Opportunity</th>
                                        <th style={s.th}>Customer</th>
                                        <th style={s.thRight}>Value (₹ Cr)</th>
                                        <th style={s.th}>Close Date</th>
                                        <th style={s.th}>Stage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topOpportunities.map((o) => (
                                        <tr key={o.name}>
                                            <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{o.name}</td>
                                            <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{o.customer}</td>
                                            <td style={s.tdRight}>{fmt(o.value)}</td>
                                            <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{o.close}</td>
                                            <td style={s.td}>
                                                <span style={s.badge(stageBadgeStyle[o.stage] || { background: '#f3f4f6', color: '#4b5563' })}>
                                                    {o.stage}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={s.viewAll}>View All Opportunities →</div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>Win / Loss Summary</h3>
                            <span style={s.cardSubtitle}>(This Month)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', padding: '0 20px 16px' }}>
                            <div style={{ width: 130, height: 130, position: 'relative', flexShrink: 0 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={winLoss} dataKey="value" nameKey="name" innerRadius={38} outerRadius={58} paddingAngle={2}>
                                            {winLoss.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{winLossTotal}</div>
                                    <div style={{ fontSize: 10, color: '#9ca3af' }}>Total</div>
                                </div>
                            </div>
                            <div style={{ flex: 1, paddingLeft: 16 }}>
                                {winLoss.map((w) => (
                                    <div key={w.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#4b5563' }}>
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: w.color, display: 'inline-block' }} />
                                            {w.name}
                                        </span>
                                        <span style={{ color: '#111827', fontWeight: 500 }}>{w.value} ({w.pct.toFixed(1)}%)</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={s.viewAll}>View Detailed Analysis →</div>
                    </div>
                </div>

                <p style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', paddingTop: 8 }}>
                    All amounts are in ₹ Crore &nbsp;|&nbsp; Data is real-time and synchronized across all plants
                </p>
            </div>
        </Layout>
    );
}

export default CSODashboard;